// ============================================
// pages/reports/UtilizationReconciliationPage.tsx
// Per-district received-vs-spent breakdown across 11 utilization heads.
// 5 heads are computed live from proforma ledgers; 6 are DM-entered.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { Scale, Loader2, XCircle, Download, AlertTriangle } from 'lucide-react';
import { seasonsAPI, reconciliationAPI } from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

interface Row {
  district_id: number;
  district_name: string;
  district_code: string;
  received: number;
  drawdown_count: number;
  computed: {
    farmers_payment_rs: number;
    gunnies_payment_rs: number;
    transportation_payment_rs: number;
    unloading_payment_rs: number;
    storage_charges_rs: number;
    sources: {
      farmer_payments_count: number;
      gunny_utilization_count: number;
      transport_bills_count: number;
      unloading_bills_count: number;
      storage_bills_count: number;
    };
  };
  stored: {
    fertiliser_companies_rs: number;
    fertiliser_ht_rs: number;
    other_loan_interest_rs: number;
    monthly_interest_rs: number;
    other_loan_repayments_rs: number;
    others_rs: number;
  };
  spent_computed: number;
  spent_dm: number;
  spent_total: number;
  balance: number;
  is_over: boolean;
}

export const UtilizationReconciliationPage: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [data, setData] = useState<{ rows: Row[]; totals: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyActive, setOnlyActive] = useState(true);  // show only districts with drawdowns

  useEffect(() => {
    (async () => {
      try {
        const s = await seasonsAPI.list();
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load seasons');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await reconciliationAPI.utilization(selectedSeasonId);
        setData(res.data as any);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load reconciliation');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (!onlyActive) return data.rows;
    return data.rows.filter((r) => r.received > 0 || r.spent_total > 0);
  }, [data, onlyActive]);

  const handlePrint = () => window.print();

  if (loading && !data) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto print:max-w-none">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Scale className="w-7 h-7 text-blue-600" />
            Utilization Reconciliation
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Per-district received vs spent. <span className="font-semibold text-blue-700">AUTO</span> cols live-computed from proforma ledgers; DM cols from the utilization form.
          </p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg text-sm">
          <Download className="w-4 h-4" />Print
        </button>
      </div>

      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end print:hidden">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
          <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
          Show only districts with activity
        </label>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" />{error}</div>}

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Received from HO</p>
            <p className="text-2xl font-bold text-gray-900">{formatAmount(num(data.totals.received))}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Spent — computed</p>
            <p className="text-2xl font-bold text-blue-700">{formatAmount(num(data.totals.spent_computed))}</p>
            <p className="text-[10px] text-gray-400 mt-1">from proforma ledgers</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Spent — DM entered</p>
            <p className="text-2xl font-bold text-green-700">{formatAmount(num(data.totals.spent_dm))}</p>
            <p className="text-[10px] text-gray-400 mt-1">cols 19-24</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Balance on Hand</p>
            <p className={`text-2xl font-bold ${data.totals.balance < 0 ? 'text-red-600' : 'text-orange-600'}`}>
              {formatAmount(num(data.totals.balance))}
            </p>
          </div>
        </div>
      )}

      {/* Per-district table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan={2} className="px-3 py-2 text-left font-semibold text-gray-600 uppercase border-r border-gray-200">District</th>
                <th rowSpan={2} className="px-3 py-2 text-right font-semibold text-gray-600 uppercase border-r border-gray-200">Received</th>
                <th colSpan={5} className="px-3 py-1 text-center font-semibold text-blue-700 uppercase border-r border-gray-200 bg-blue-50">AUTO (from proformas)</th>
                <th colSpan={6} className="px-3 py-1 text-center font-semibold text-green-700 uppercase border-r border-gray-200 bg-green-50">DM-ENTERED</th>
                <th rowSpan={2} className="px-3 py-2 text-right font-semibold text-gray-600 uppercase">Total Spent</th>
                <th rowSpan={2} className="px-3 py-2 text-right font-semibold text-gray-600 uppercase">Balance</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-right font-semibold text-blue-700 uppercase bg-blue-50">Farmers</th>
                <th className="px-2 py-2 text-right font-semibold text-blue-700 uppercase bg-blue-50">Gunnies</th>
                <th className="px-2 py-2 text-right font-semibold text-blue-700 uppercase bg-blue-50">Transport</th>
                <th className="px-2 py-2 text-right font-semibold text-blue-700 uppercase bg-blue-50">Unloading</th>
                <th className="px-2 py-2 text-right font-semibold text-blue-700 uppercase bg-blue-50 border-r border-gray-200">Storage</th>
                <th className="px-2 py-2 text-right font-semibold text-green-700 uppercase bg-green-50">Fert Co</th>
                <th className="px-2 py-2 text-right font-semibold text-green-700 uppercase bg-green-50">Fert H&amp;T</th>
                <th className="px-2 py-2 text-right font-semibold text-green-700 uppercase bg-green-50">Other Int</th>
                <th className="px-2 py-2 text-right font-semibold text-green-700 uppercase bg-green-50">Mo. Int</th>
                <th className="px-2 py-2 text-right font-semibold text-green-700 uppercase bg-green-50">Repay</th>
                <th className="px-2 py-2 text-right font-semibold text-green-700 uppercase bg-green-50 border-r border-gray-200">Others</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.length === 0 ? (
                <tr><td colSpan={15} className="px-4 py-12 text-center text-gray-400">No activity in this season.</td></tr>
              ) : filteredRows.map((r) => (
                <tr key={r.district_id} className={`hover:bg-gray-50 ${r.is_over ? 'bg-red-50' : ''}`}>
                  <td className="px-3 py-2 border-r border-gray-100">
                    <div className="font-semibold text-gray-900">{r.district_name}</div>
                    {r.drawdown_count > 0 && <div className="text-[10px] text-gray-400">{r.drawdown_count} drawdown{r.drawdown_count !== 1 ? 's' : ''}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900 border-r border-gray-100">{formatAmount(num(r.received))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50/30">{formatAmount(num(r.computed.farmers_payment_rs))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50/30">{formatAmount(num(r.computed.gunnies_payment_rs))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50/30">{formatAmount(num(r.computed.transportation_payment_rs))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50/30">{formatAmount(num(r.computed.unloading_payment_rs))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50/30 border-r border-gray-200">{formatAmount(num(r.computed.storage_charges_rs))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50/30">{formatAmount(num(r.stored.fertiliser_companies_rs))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50/30">{formatAmount(num(r.stored.fertiliser_ht_rs))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50/30">{formatAmount(num(r.stored.other_loan_interest_rs))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50/30">{formatAmount(num(r.stored.monthly_interest_rs))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50/30">{formatAmount(num(r.stored.other_loan_repayments_rs))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50/30 border-r border-gray-200">{formatAmount(num(r.stored.others_rs))}</td>
                  <td className="px-3 py-2 text-right font-bold text-gray-900">{formatAmount(num(r.spent_total))}</td>
                  <td className={`px-3 py-2 text-right font-bold ${r.balance < 0 ? 'text-red-600' : 'text-orange-600'}`}>
                    {formatAmount(num(r.balance))}
                    {r.is_over && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                  </td>
                </tr>
              ))}
            </tbody>
            {data && filteredRows.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr className="font-bold">
                  <td className="px-3 py-2 border-r border-gray-200">TOTAL</td>
                  <td className="px-3 py-2 text-right border-r border-gray-200">{formatAmount(num(data.totals.received))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50">{formatAmount(num(data.totals.farmers_payment))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50">{formatAmount(num(data.totals.gunnies_payment))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50">{formatAmount(num(data.totals.transportation_payment))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50">{formatAmount(num(data.totals.unloading_payment))}</td>
                  <td className="px-2 py-2 text-right text-blue-700 bg-blue-50 border-r border-gray-200">{formatAmount(num(data.totals.storage_charges))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50">{formatAmount(num(data.totals.fertiliser_companies))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50">{formatAmount(num(data.totals.fertiliser_ht))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50">{formatAmount(num(data.totals.other_loan_interest))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50">{formatAmount(num(data.totals.monthly_interest))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50">{formatAmount(num(data.totals.other_loan_repayments))}</td>
                  <td className="px-2 py-2 text-right text-green-700 bg-green-50 border-r border-gray-200">{formatAmount(num(data.totals.others))}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(num(data.totals.spent_total))}</td>
                  <td className={`px-3 py-2 text-right ${data.totals.balance < 0 ? 'text-red-600' : 'text-orange-600'}`}>
                    {formatAmount(num(data.totals.balance))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500 italic">
        <span className="text-blue-700 font-semibold">AUTO</span> columns are computed live from the underlying proforma tables
        (farmer_payments, gunny_utilization, transport_bills, unloading_bills, storage_bills).
        <span className="text-green-700 font-semibold ml-2">DM-ENTERED</span> columns are from the Utilization form's cols 19-24.
        Rows highlighted in red indicate over-utilization (spent &gt; received).
      </div>
    </div>
  );
};

export default UtilizationReconciliationPage;
